/**
 * Facebook Pixel Analytics Plugin
 * 
 * Isolated plugin responsible solely for Meta/Facebook Pixel browser events.
 * Fully decoupled from TikTok, GTM, and application UI components.
 */

import { AnalyticsPlugin, AnalyticsEventPayload } from '../types';

// Currencies officially supported in client-side Meta Pixel (fbevents.js whitelist)
const META_SUPPORTED_CURRENCIES = new Set([
  'AED', 'ARS', 'AUD', 'BOB', 'BRL', 'CAD', 'CHF', 'CLP', 'CNY', 'COP',
  'CRC', 'CZK', 'DKK', 'EUR', 'GBP', 'GTQ', 'HKD', 'HNL', 'HUF', 'IDR',
  'ILS', 'INR', 'ISK', 'JPY', 'KRW', 'MOP', 'MXN', 'MYR', 'NIO', 'NOK',
  'NZD', 'PEN', 'PHP', 'PLN', 'PYG', 'QAR', 'RON', 'RUB', 'SAR', 'SEK',
  'SGD', 'THB', 'TRY', 'TWD', 'USD', 'UYU', 'VEF', 'VND', 'ZAR',
]);

export class FacebookPlugin implements AnalyticsPlugin {
  readonly name = 'facebook';
  private maxRetries = 3;

  isReady(): boolean {
    return typeof window !== 'undefined' && typeof window.fbq === 'function';
  }

  async track(eventName: string, payload: AnalyticsEventPayload): Promise<void> {
    if (typeof window === 'undefined') return;

    // 1. Prepare Meta-compliant payload
    const fbPayload: Record<string, any> = { ...payload };

    // Format value: ensure it is a valid floating point number
    if (fbPayload.value !== undefined && fbPayload.value !== null) {
      const rawVal = typeof fbPayload.value === 'number'
        ? fbPayload.value
        : parseFloat(String(fbPayload.value).replace(/[^0-9.-]+/g, ''));
      fbPayload.value = isNaN(rawVal) ? 0 : rawVal;
    }

    // Format currency: Meta Pixel strictly requires a currency in its hardcoded whitelist.
    // BDT is not supported by browser fbevents.js, so convert value to USD for Meta Ads optimization.
    if (fbPayload.currency) {
      const upperCurrency = String(fbPayload.currency).trim().toUpperCase();
      if (META_SUPPORTED_CURRENCIES.has(upperCurrency)) {
        fbPayload.currency = upperCurrency;
      } else {
        // Fallback to USD (standard for Meta Ad accounts in Bangladesh)
        const usdRate = parseFloat(localStorage.getItem('usd_to_bdt_rate') || '125') || 125;
        if (typeof fbPayload.value === 'number' && fbPayload.value > 0) {
          fbPayload.value = Number((fbPayload.value / usdRate).toFixed(2));
        }
        fbPayload.currency = 'USD';
      }
    }

    // Ensure content_type is present for e-commerce events
    if (!fbPayload.content_type && (fbPayload.content_id || fbPayload.content_ids)) {
      fbPayload.content_type = 'product';
    }

    // Deduplication Event ID
    const eventId = fbPayload.event_id || `${eventName}_${Date.now()}`;
    const options = { eventID: eventId };

    // Advanced Matching: set user properties if phone/email is available
    const rawPhone = payload.phone || payload.phone_number || payload.customer_phone || '';
    const rawEmail = payload.email || payload.customer_email || '';
    if (rawPhone || rawEmail) {
      try {
        const userData: Record<string, string> = {};
        if (rawEmail) {
          userData.em = String(rawEmail).trim().toLowerCase();
        }
        if (rawPhone) {
          const digits = String(rawPhone).replace(/\D/g, '');
          userData.ph = digits.startsWith('880') ? digits : (digits.startsWith('0') ? `88${digits}` : digits);
        }
        if (typeof window.fbq === 'function') {
          window.fbq('setUserProperties', userData);
        }
      } catch (e) {
        // Non-blocking
      }
    }

    let attempts = 0;
    const execute = (): Promise<void> => {
      return new Promise((resolve) => {
        const attemptTrack = () => {
          if (this.isReady()) {
            window.fbq('track', eventName, fbPayload, options);
            resolve();
          } else if (attempts < this.maxRetries) {
            attempts++;
            setTimeout(attemptTrack, 100);
          } else {
            // FB Pixel not loaded or not configured — resolve gracefully
            resolve();
          }
        };
        attemptTrack();
      });
    };

    await execute();
  }
}
